import React, { useContext, useState } from "react";
import { Modal } from "reactstrap";
import Icon from "@mdi/react";
import { mdiChevronLeft, mdiClose } from "@mdi/js";
import PricingView from "./PricingView";
import DowngradeView from "./DowngradeView";
import MobileSubView from "./MobileSubView";
import UpgradeView from "./UpgradeView";
import {
  mobileSubscriptionLookup,
  webSubscriptionLookup,
  ACTIVE_PRICING_DURATIONS,
  ACTIVE_MOBILE_PRODUCTS,
  useUserSubscriptions,
  SubscriptionContext,
} from "src/services/SubscriptionService";
import AnalyticsService from "src/services/AnalyticsService";
import { DEFAULT_TWO_SPORTS } from "src/constants";
import { TierItem, DurationItem } from "src/typings/pricing";
import styles from "./styles.scss";
import { useEffect } from "react";

const tiers: any = {
  prem: 0,
  pro: 1,
  vip: 2,
  sharp: 3,
};

export interface ProductOptions {
  sportList?: string[];
  selectedTier?: TierItem;
  selectedPeriod?: DurationItem;
  showDowngrade?: boolean;
  showUpgrade?: boolean;
}

export interface PricingModalProps {
  visible: boolean;
  toggleModal(): void;
}

const initState = {
  sportList: DEFAULT_TWO_SPORTS,
  selectedTier: ACTIVE_MOBILE_PRODUCTS[1][0], // web_betql_pro_month
  selectedPeriod: ACTIVE_PRICING_DURATIONS[0], // monthly
  showDowngrade: false,
  showUpgrade: false,
  disabled: false,
  tabIndex: 1,
};

const PricingModal = () => {
  const ctx = useContext(SubscriptionContext);
  const visible = ctx.pricingModalVisible;
  const userSubscriptions = useUserSubscriptions({ skip: !visible });
  const [selectedTier, setSelectedTier] = useState(initState.selectedTier); // prettier-ignore
  const [sportList, setSportList] = useState(initState.sportList);
  const [selectedPeriod, setSelectedPeriod] = useState(initState.selectedPeriod); // prettier-ignore
  const [showUpgrade, setShowUpgrade] = useState(initState.showUpgrade);
  const [showDowngrade, setShowDowngrade] = useState(initState.showDowngrade);
  const [disabled, setDisabled] = useState(initState.disabled);
  const [tabIndex, setTabIndex] = useState(initState.tabIndex);

  // Reset on close
  useEffect(() => {
    if (visible) {
      return;
    }
    setState(initState);
  }, [visible]);

  useEffect(() => {
    if (visible) {
      AnalyticsService.track("Pricing Modal View", {
        screen: "PricingModal",
        tab: selectedTier.durationTitle,
        subscriptionLevel: `${selectedTier.title} ${selectedTier.durationTitle}`,
        step: ["", "Pricing", "Selection"][tabIndex] || "Pricing",
        upgradeSource: ctx.subscriptionUpgradeSource,
      });
    }
  }, [visible, tabIndex]);

  // Current sub logic (old)
  const isMobilePlan: boolean = ctx.subscriptionLevel ? Boolean(mobileSubscriptionLookup[ctx.subscriptionLevel]) : false; // prettier-ignore
  const subscriptionLevel: string = !isMobilePlan && ctx.subscriptionLevel ? webSubscriptionLookup[ctx.subscriptionLevel] : ""; // prettier-ignore
  const userSubscription = userSubscriptions[0] || {};

  let currentTier, tierIndex = 1, productIndex = 0, isSubscribed = false; // prettier-ignore

  for (let key in tiers) {
    if (subscriptionLevel?.includes(key)) {
      tierIndex = tiers[key];
    }
  }

  for (let j = 0; j < ACTIVE_MOBILE_PRODUCTS[tierIndex].length; j++) {
    if (subscriptionLevel === ACTIVE_MOBILE_PRODUCTS[tierIndex][j].key) {
      productIndex = j;
      currentTier = ACTIVE_MOBILE_PRODUCTS[tierIndex][productIndex];
      isSubscribed = true;
      break;
    }
  }

  const currentTierValue = isSubscribed ? Math.pow(10, tierIndex) + productIndex : undefined; // prettier-ignore

  const setState = (options: any) => {
    options.sportList !== undefined && setSportList(options.sportList);
    options.selectedTier !== undefined && setSelectedTier(options.selectedTier);
    options.selectedPeriod !== undefined && setSelectedPeriod(options.selectedPeriod); // prettier-ignore
    options.showUpgrade !== undefined && setShowUpgrade(options.showUpgrade); // prettier-ignore
    options.showDowngrade !== undefined && setShowDowngrade(options.showDowngrade); // prettier-ignore
    options.disabled !== undefined && setDisabled(options.disabled);
    options.tabIndex !== undefined && setTabIndex(options.tabIndex);
  };

  const onSelect = (options: ProductOptions) => {
    setState(options);
    setTabIndex(tabIndex + 1);
  };

  const toggleModal = () => ctx.togglePricingModal({});

  // Modals should not be in the DOM if not visible
  if (!visible) {
    return null;
  }

  return (
    <Modal
      className="rotoql-pricing-modal__dialog"
      modalClassName="rotoql-pricing-modal"
      wrapClassName="rotoql-pricing-modal__wrapper"
      backdropClassName="rotoql-pricing-modal__backdrop"
      isOpen={visible}
      slide="false"
      size="lg"
      centered
    >
      <React.Fragment>
        {tabIndex === 2 && (
          <div className="rotoql-pricing-modal__back" onClick={() => setTabIndex(1)}>
            <Icon path={mdiChevronLeft} size="26px" color="#262626" />
            Back
          </div>
        )}
        <div className="rotoql-pricing-modal__close" onClick={() => !disabled && toggleModal()}>
          <Icon path={mdiClose} size="20px" color="#262626" />
        </div>
        {isMobilePlan ? (
          <MobileSubView toggleModal={toggleModal} />
        ) : (
          <React.Fragment>
            {tabIndex === 1 && (
              <PricingView
                onSelectProduct={onSelect}
                subscriptionLevel={subscriptionLevel}
                userSubscription={userSubscription}
                currentTierValue={currentTierValue}
                currentTier={currentTier}
                source={ctx.subscriptionUpgradeSource}
                duration={selectedPeriod}
                tierIndex={ctx.subscriptionDefaultTierIndex ?? undefined}
                sportList={ctx.subscriptionDefaultSports || undefined}
              />
            )}
            {tabIndex === 2 && (
              <React.Fragment>
                {showUpgrade ? (
                  <UpgradeView
                    currentTier={currentTier}
                    selectedTier={selectedTier}
                    toggleModal={toggleModal}
                    userSubscription={userSubscription}
                    sportList={sportList}
                  />
                ) : null}
                {showDowngrade ? (
                  <DowngradeView
                    currentTier={currentTier}
                    selectedTier={selectedTier}
                    toggleModal={toggleModal}
                    userSubscription={userSubscription}
                    sportList={sportList}
                  />
                ) : null}
              </React.Fragment>
            )}
          </React.Fragment>
        )}
        <style jsx>{styles}</style>
      </React.Fragment>
    </Modal>
  );
};

export default PricingModal;
